import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { axiosInstance } from '@/App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Building2, User, Mail, Lock, Phone, MapPin, Eye, EyeOff, CheckCircle2, ArrowLeft } from 'lucide-react';

function Registro() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1); // 1: Seleccionar plan, 2: Datos empresa, 3: Checkout
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    business_name: '',
    business_nif: '',
    business_phone: '',
    business_email: '',
    business_address: '',
    contact_name: '',
    contact_email: '',
    password: '',
    confirmPassword: '',
    plan_id: ''
  });

  useEffect(() => {
    loadPlans();
    
    // Si viene plan en URL, pre-seleccionarlo
    const planParam = searchParams.get('plan');
    if (planParam) {
      // Se seleccionará después de cargar los planes
    }
  }, []);

  const loadPlans = async () => {
    try {
      const response = await axiosInstance.get('/plans');
      setPlans(response.data.plans || []);
      
      // Pre-seleccionar plan de URL
      const planParam = searchParams.get('plan');
      if (planParam) {
        const plan = response.data.plans.find(p => p.slug === planParam);
        if (plan) {
          setSelectedPlan(plan);
          setFormData(prev => ({ ...prev, plan_id: plan.id }));
        }
      }
    } catch (error) {
      console.error('Error cargando planes:', error);
      toast.error('Error al cargar los planes');
    }
  };

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setFormData({ ...formData, plan_id: plan.id });
    setStep(2);
  };

  const validateForm = () => {
    if (!formData.business_name.trim()) {
      toast.error('El nombre de la empresa es obligatorio');
      return false;
    }
    if (!formData.business_phone.trim()) {
      toast.error('El teléfono es obligatorio');
      return false;
    }
    if (!formData.business_email.trim()) {
      toast.error('El email de la empresa es obligatorio');
      return false;
    }
    if (!formData.contact_name.trim()) {
      toast.error('El nombre de contacto es obligatorio');
      return false;
    }
    if (!formData.contact_email.trim()) {
      toast.error('El email de contacto es obligatorio');
      return false;
    }
    if (!formData.password || formData.password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      const registerData = {
        business_name: formData.business_name,
        business_nif: formData.business_nif || null,
        business_phone: formData.business_phone,
        business_email: formData.business_email,
        business_address: formData.business_address || null,
        contact_name: formData.contact_name,
        contact_email: formData.contact_email,
        password: formData.password,
        plan_id: formData.plan_id
      };

      const response = await axiosInstance.post('/register-business', registerData);
      
      toast.success('¡Empresa registrada correctamente!');
      
      // Redirigir al login con mensaje
      setTimeout(() => {
        navigate('/login?registered=true');
      }, 2000);
      
    } catch (error) {
      console.error('Error registrando empresa:', error);
      toast.error(error.response?.data?.detail || 'Error al registrar la empresa');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <Button
            variant="ghost"
            onClick={() => step === 1 ? navigate('/') : setStep(1)}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver
          </Button>
          <h1 className="text-4xl font-bold text-slate-900 mb-2">
            Crear cuenta en OSAGEST
          </h1>
          <p className="text-lg text-slate-600">
            14 días de prueba gratuita • Sin tarjeta de crédito
          </p>
        </div>

        {/* Steps Indicator */}
        <div className="max-w-3xl mx-auto mb-8">
          <div className="flex items-center justify-center gap-4">
            <div className={`flex items-center ${step >= 1 ? 'text-blue-600' : 'text-slate-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-slate-300'}`}>
                1
              </div>
              <span className="ml-2 hidden sm:inline">Seleccionar Plan</span>
            </div>
            <div className="h-0.5 w-12 bg-slate-300"></div>
            <div className={`flex items-center ${step >= 2 ? 'text-blue-600' : 'text-slate-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-slate-300'}`}>
                2
              </div>
              <span className="ml-2 hidden sm:inline">Datos de Empresa</span>
            </div>
          </div>
        </div>

        {/* Step 1: Seleccionar Plan */}
        {step === 1 && (
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-center mb-8">Elige tu plan</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {plans.map((plan) => (
                <Card 
                  key={plan.id}
                  className={`cursor-pointer hover:shadow-xl transition-all ${
                    selectedPlan?.id === plan.id ? 'border-2 border-blue-500' : ''
                  } ${plan.is_popular ? 'border-2 border-blue-500' : ''}`}
                  onClick={() => handleSelectPlan(plan)}
                >
                  {plan.is_popular && (
                    <div className="bg-blue-500 text-white text-center py-2 font-semibold text-sm">
                      MÁS POPULAR
                    </div>
                  )}
                  <CardHeader className="text-center">
                    <CardTitle className="text-2xl">{plan.name}</CardTitle>
                    <div className="mt-4">
                      <span className="text-5xl font-bold">{plan.price_monthly}€</span>
                      <span className="text-slate-600">/mes</span>
                    </div>
                    <p className="text-sm text-slate-500 mt-2">
                      {plan.trial_days} días de prueba gratis
                    </p>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start text-sm">
                          <CheckCircle2 className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button className="w-full mt-6" variant={plan.is_popular ? "default" : "outline"}>
                      Seleccionar {plan.name}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Formulario de Registro */}
        {step === 2 && selectedPlan && (
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardHeader>
                <CardTitle>Datos de tu Empresa</CardTitle>
                <CardDescription>
                  Plan seleccionado: <strong>{selectedPlan.name}</strong> - {selectedPlan.price_monthly}€/mes
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Datos de la Empresa */}
                  <div className="border-b pb-6">
                    <h3 className="text-lg font-semibold mb-4 flex items-center">
                      <Building2 className="h-5 w-5 mr-2" />
                      Datos de la Empresa
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="business_name">Nombre de la Empresa *</Label>
                        <Input
                          id="business_name"
                          value={formData.business_name}
                          onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                          placeholder="Mi Empresa S.L."
                          required
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="business_nif">NIF/CIF</Label>
                          <Input
                            id="business_nif"
                            value={formData.business_nif}
                            onChange={(e) => setFormData({ ...formData, business_nif: e.target.value })}
                            placeholder="B12345678"
                          />
                        </div>
                        <div>
                          <Label htmlFor="business_phone">Teléfono *</Label>
                          <Input
                            id="business_phone"
                            value={formData.business_phone}
                            onChange={(e) => setFormData({ ...formData, business_phone: e.target.value })}
                            placeholder="+34 XXX XXX XXX"
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="business_email">Email de la Empresa</Label>
                        <Input
                          id="business_email"
                          type="email"
                          value={formData.business_email}
                          onChange={(e) => setFormData({ ...formData, business_email: e.target.value })}
                          placeholder="info@miempresa.com"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="business_address">Dirección Fiscal</Label>
                        <Input
                          id="business_address"
                          value={formData.business_address}
                          onChange={(e) => setFormData({ ...formData, business_address: e.target.value })}
                          placeholder="Calle, número, ciudad, CP"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Persona de Contacto */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4 flex items-center">
                      <User className="h-5 w-5 mr-2" />
                      Persona de Contacto (Administrador)
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="contact_name">Nombre Completo *</Label>
                        <Input
                          id="contact_name"
                          value={formData.contact_name}
                          onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                          placeholder="Juan Pérez"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="contact_email">Email (Usuario de acceso) *</Label>
                        <Input
                          id="contact_email"
                          type="email"
                          value={formData.contact_email}
                          onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                          placeholder="juan@miempresa.com"
                          required
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Este email será tu usuario para iniciar sesión
                        </p>
                      </div>
                      <div>
                        <Label htmlFor="password">Contraseña *</Label>
                        <div className="relative">
                          <Input
                            id="password"
                            type={showPassword ? "text" : "password"}
                            value={formData.password}
                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                            placeholder="Mínimo 6 caracteres"
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-700"
                          >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="confirmPassword">Confirmar Contraseña *</Label>
                        <div className="relative">
                          <Input
                            id="confirmPassword"
                            type={showConfirmPassword ? "text" : "password"}
                            value={formData.confirmPassword}
                            onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                            placeholder="Repite la contraseña"
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-700"
                          >
                            {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Botones */}
                  <div className="flex gap-4 pt-6 border-t">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setStep(1)}
                      className="flex-1"
                    >
                      Cambiar Plan
                    </Button>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="flex-1"
                    >
                      {loading ? 'Creando cuenta...' : 'Crear mi cuenta'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            <p className="text-center text-sm text-slate-600 mt-4">
              ¿Ya tienes una cuenta?{' '}
              <button
                onClick={() => navigate('/login')}
                className="text-blue-600 hover:underline font-semibold"
              >
                Inicia sesión aquí
              </button>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Registro;
